import { useCallback, useEffect, useState } from 'react';
import { partnerApiService } from '../../services/api';
import { MenuCategory, MenuItem } from '../../types/api';

export type CatalogStatus = 'loading' | 'ready' | 'error';

export interface DiscountCatalog {
  status: CatalogStatus;
  categories: MenuCategory[];
  items: MenuItem[];
  reload: () => void;
}

interface CatalogState {
  status: CatalogStatus;
  categories: MenuCategory[];
  items: MenuItem[];
}

/**
 * Меню целиком для формы скидки: разделы и все позиции с ценами.
 *
 * Раньше дерево подгружало позиции раздела по клику — и цены выбранных
 * позиций форма не знала вовсе. Превью «было → стало», сумма комбо и
 * проверка «тенге не больше самой дешёвой» требуют цен сразу, поэтому меню
 * грузится одним заходом при открытии формы.
 */
export function useDiscountCatalog(): DiscountCatalog {
  const [state, setState] = useState<CatalogState>({ status: 'loading', categories: [], items: [] });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading' }));

    Promise.all([partnerApiService.getMenuCategories(), partnerApiService.getAllMenuItems()])
      .then(([categories, items]) => {
        if (!cancelled) setState({ status: 'ready', categories, items });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error('Ошибка загрузки меню для скидки:', error);
        setState((prev) => ({ ...prev, status: 'error' }));
      });

    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((prev) => prev + 1), []);

  return { ...state, reload };
}
